// Commitment link helper — Commitments-Modal-01.
//
// Centralises the "open Commitment X as a route-overlay modal"
// link contract so every surface (Tracker row, Kanban card,
// Account commitments panel, dashboard widgets, future BeneAI
// surfaces) builds the same ``to`` + ``state`` shape. Without a
// shared helper each surface has to remember to thread
// ``backgroundLocation`` through, the modal silently falls back
// to a full-page navigation, and filters / scroll get clobbered.
//
// Usage:
//
//   const linkProps = useCommitmentLinkProps();
//   <Link {...linkProps(commitmentId)}>{title}</Link>
//
// or for a one-off non-hook context:
//
//   buildCommitmentLinkProps(commitmentId, location, { fromLabel })
//
// The helper deliberately stays tiny — surface code already owns
// its own ``location`` instance via ``useLocation()`` and we don't
// want a second source of truth for "the URL the user is on".

import type { Location } from "react-router-dom";
import { useLocation } from "react-router-dom";


export interface CommitmentLinkOptions {
  /** Optional human label paired with the background URL. The
   * detail page's Back link reads it when the modal falls back
   * to the full-page route (direct URL load / refresh). */
  fromLabel?: string;
}


export interface CommitmentLinkProps {
  to: string;
  state: {
    backgroundLocation: Location;
    fromUrl: string;
    fromLabel?: string;
  };
}


/** Build a ``Link`` ``to`` + ``state`` pair that opens the given
 * Commitment as a route-overlay modal. ``location`` is the URL
 * the user is currently on (read from ``useLocation()``). */
export function buildCommitmentLinkProps(
  commitmentId: string,
  location: Location,
  options: CommitmentLinkOptions = {},
): CommitmentLinkProps {
  return {
    to: `/dashboard/commitments/${commitmentId}`,
    state: {
      backgroundLocation: location,
      fromUrl: `${location.pathname}${location.search}`,
      ...(options.fromLabel ? { fromLabel: options.fromLabel } : {}),
    },
  };
}


/** React hook that returns a stable factory: pass it a Commitment
 * id (and optional metadata) and get back the ``{ to, state }``
 * pair to spread onto a ``<Link>``. Reads ``location`` from
 * ``useLocation`` so callers don't have to thread it themselves. */
export function useCommitmentLinkProps(): (
  commitmentId: string,
  options?: CommitmentLinkOptions,
) => CommitmentLinkProps {
  const location = useLocation();
  return (commitmentId, options) =>
    buildCommitmentLinkProps(commitmentId, location, options);
}
